interface Retrocession {
  amount: number;
  distribution_type: string;
  primary_negotiator_id: string | null;
  secondary_negotiator_id: string | null;
  tertiary_negotiator_id: string | null;
  quaternary_negotiator_id: string | null;
  distribution_percentages: Record<string, number> | null;
}

export const DISTRIBUTION_KEY_SEPARATOR = '|';

/** Suffixe ajouté quand deux lignes partagent le même couple négociateur + agence */
const OCCURRENCE_SEPARATOR = '#';

/**
 * Clé utilisée dans distribution_percentages.
 * Un négociateur peut apparaître plusieurs fois sur une même transaction (une ligne
 * par agence). La clé est donc "negociateurId|AGENCE" dès qu'une agence est connue,
 * et l'ancien format "negociateurId" reste lisible (données historiques).
 */
export function buildDistributionKey(negotiatorId: string, agency?: string | null): string {
  return agency ? `${negotiatorId}${DISTRIBUTION_KEY_SEPARATOR}${agency}` : negotiatorId;
}

export function parseDistributionKey(key: string): { negotiatorId: string; agency: string | null } {
  const base = key.split(OCCURRENCE_SEPARATOR)[0];
  const separatorIndex = base.indexOf(DISTRIBUTION_KEY_SEPARATOR);
  if (separatorIndex === -1) return { negotiatorId: base, agency: null };
  return {
    negotiatorId: base.slice(0, separatorIndex),
    agency: base.slice(separatorIndex + 1)
  };
}

/**
 * Clés de répartition d'une liste de lignes « négociateur », dans l'ordre.
 * Une ligne = un négociateur sur une agence ; le suffixe #n départage deux lignes
 * strictement identiques.
 */
export function buildDistributionLineKeys(
  lines: { id: string; agency?: string | null }[]
): string[] {
  const seen = new Map<string, number>();
  return lines.map(line => {
    const base = buildDistributionKey(line.id, line.agency);
    const occurrence = seen.get(base) || 0;
    seen.set(base, occurrence + 1);
    return occurrence === 0 ? base : `${base}${OCCURRENCE_SEPARATOR}${occurrence}`;
  });
}

/** IDs de négociateurs présents dans une répartition (sans le suffixe d'agence, dédoublonnés). */
export function getDistributionNegotiatorIds(
  percentages: Record<string, number> | null | undefined
): string[] {
  if (!percentages) return [];
  const ids = Object.keys(percentages)
    .filter(key => key !== 'total')
    .map(key => parseDistributionKey(key).negotiatorId);
  return Array.from(new Set(ids));
}

/**
 * Part d'une ligne transaction_negotiators précise (négociateur + agence).
 * Retombe sur l'ancien format (clé = id seul) uniquement s'il n'existe aucune
 * répartition par agence pour ce négociateur, afin de ne jamais compter deux fois.
 */
export function getRowDistributionPercentage(
  percentages: Record<string, number> | null | undefined,
  negotiatorId: string,
  agency?: string | null
): number {
  if (!percentages) return 0;

  const compositeValue = percentages[buildDistributionKey(negotiatorId, agency)];
  if (agency && typeof compositeValue === 'number') return compositeValue;

  const hasAgencySplit = Object.keys(percentages).some(
    key => key !== 'total' && key.startsWith(`${negotiatorId}${DISTRIBUTION_KEY_SEPARATOR}`)
  );
  if (hasAgencySplit) return 0;

  const legacyValue = percentages[negotiatorId];
  return typeof legacyValue === 'number' ? legacyValue : 0;
}

/** Part totale d'un négociateur sur une rétrocession, toutes agences confondues. */
export function getNegotiatorDistributionPercentage(
  percentages: Record<string, number> | null | undefined,
  negotiatorId: string
): number {
  if (!percentages) return 0;
  return Object.entries(percentages).reduce((sum, [key, value]) => {
    if (key === 'total' || typeof value !== 'number') return sum;
    return parseDistributionKey(key).negotiatorId === negotiatorId ? sum + value : sum;
  }, 0);
}

interface NegotiatorRow {
  negotiator_id: string;
  agency?: string | null;
}

export interface RetrocessionCheck {
  distribution_type: string;
  primary_negotiator_id?: string | null;
  secondary_negotiator_id?: string | null;
  tertiary_negotiator_id?: string | null;
  quaternary_negotiator_id?: string | null;
  distribution_percentages?: Record<string, number> | null;
}

/** Tolérance sur le total d'une répartition (arrondis de saisie) */
const TOTAL_TOLERANCE = 0.01;

/**
 * Garde-fou : les négociateurs d'une rétrocession doivent être ceux de la vente.
 *
 * Si un négociateur est remplacé après la saisie des rétrocessions, la répartition
 * continue sinon de désigner l'ancien : le montant n'est plus imputé à personne et
 * disparaît des statistiques.
 *
 * Retourne la liste des anomalies, en clair. Vide = tout est cohérent.
 */
export function checkRetrocessionsAgainstNegotiators(
  retrocessions: RetrocessionCheck[] | null | undefined,
  negotiators: { id: string }[]
): string[] {
  const known = new Set(negotiators.filter(n => n.id).map(n => n.id));
  const problems: string[] = [];

  (retrocessions || []).forEach((retrocession, index) => {
    const label = `Rétrocession #${index + 1}`;

    if (retrocession.distribution_type === 'custom') {
      const percentages = retrocession.distribution_percentages;
      const entries = Object.entries(percentages || {}).filter(([key]) => key !== 'total');

      if (entries.length === 0) {
        problems.push(`${label} : aucune répartition entre les négociateurs.`);
        return;
      }

      const removed = entries.filter(
        ([key]) => !known.has(parseDistributionKey(key).negotiatorId)
      );
      if (removed.length > 0) {
        problems.push(
          `${label} : la répartition concerne ${removed.length > 1 ? 'des négociateurs qui ne sont plus' : "un négociateur qui n'est plus"} sur la vente.`
        );
      }

      const total = entries.reduce((sum, [, value]) => sum + (Number(value) || 0), 0);
      if (Math.abs(total - 100) > TOTAL_TOLERANCE) {
        problems.push(`${label} : le total de la répartition est de ${Math.round(total * 100) / 100}% au lieu de 100%.`);
      }
      return;
    }

    // single / equal : les bénéficiaires sont désignés par colonne
    const beneficiaries = [
      retrocession.primary_negotiator_id,
      retrocession.secondary_negotiator_id,
      retrocession.tertiary_negotiator_id,
      retrocession.quaternary_negotiator_id
    ].filter(Boolean) as string[];

    if (beneficiaries.length === 0) {
      problems.push(`${label} : aucun négociateur bénéficiaire n'est désigné.`);
      return;
    }

    if (beneficiaries.some(id => !known.has(id))) {
      problems.push(`${label} : le bénéficiaire n'est plus sur la vente.`);
    }
  });

  return problems;
}

/**
 * Montant de rétrocession supporté par chaque ligne transaction_negotiators.
 *
 * Un négociateur peut avoir plusieurs lignes sur la même transaction (une par
 * agence) : sa rétrocession ne doit alors être comptée qu'une fois au total, et
 * répartie entre ses lignes — sans quoi elle est déduite deux fois.
 *
 * Retourne un tableau aligné sur `rows`.
 */
export function buildRetrocessionRowAmounts(
  retrocessions: Retrocession[] | null | undefined,
  rows: NegotiatorRow[]
): number[] {
  const amounts = new Array(rows.length).fill(0);

  const rowsOf = (negotiatorId: string) =>
    rows.map((row, i) => ({ row, i })).filter(({ row }) => row.negotiator_id === negotiatorId);

  const spreadOverRows = (negotiatorId: string, amount: number) => {
    const targets = rowsOf(negotiatorId);
    if (targets.length === 0) return;
    const share = amount / targets.length;
    targets.forEach(({ i }) => { amounts[i] += share; });
  };

  (retrocessions || []).forEach(retrocession => {
    if (retrocession.distribution_type === 'single') {
      if (retrocession.primary_negotiator_id) {
        spreadOverRows(retrocession.primary_negotiator_id, retrocession.amount);
      }
      return;
    }

    if (retrocession.distribution_type === 'equal') {
      const negotiatorIds = [
        retrocession.primary_negotiator_id,
        retrocession.secondary_negotiator_id,
        retrocession.tertiary_negotiator_id,
        retrocession.quaternary_negotiator_id
      ].filter(Boolean) as string[];

      if (negotiatorIds.length === 0) return;
      const amountPerNegotiator = retrocession.amount / negotiatorIds.length;
      negotiatorIds.forEach(id => spreadOverRows(id, amountPerNegotiator));
      return;
    }

    if (retrocession.distribution_type === 'custom' && retrocession.distribution_percentages) {
      const percentages = retrocession.distribution_percentages;

      /**
       * La ligne a-t-elle une part propre au nouveau format « id|AGENCE » ?
       * Une ligne sans agence enregistrée n'en a jamais : sa clé serait l'ID seul,
       * c'est-à-dire l'ancien format, traité juste après.
       */
      const ownShare = (row: NegotiatorRow): number | null => {
        if (!row.agency) return null;
        const value = percentages[buildDistributionKey(row.negotiator_id, row.agency)];
        return typeof value === 'number' ? value : null;
      };

      // Répartition par agence (nouveau format) : chaque ligne prend sa propre part
      rows.forEach((row, i) => {
        const value = ownShare(row);
        if (value !== null) {
          amounts[i] += (retrocession.amount * value) / 100;
        }
      });

      // Clés à l'ancien format (négociateur seul) : réparties entre ses lignes
      // restées sans part propre
      Object.entries(percentages).forEach(([key, percentage]) => {
        if (key === 'total' || typeof percentage !== 'number') return;
        if (key.includes(DISTRIBUTION_KEY_SEPARATOR)) return;

        const targets = rowsOf(key).filter(({ row }) => ownShare(row) === null);
        if (targets.length === 0) return;

        const share = (retrocession.amount * percentage) / 100 / targets.length;
        targets.forEach(({ i }) => { amounts[i] += share; });
      });
    }
  });

  return amounts;
}
